import { derivedSonata, SONATA_IDS, VARIATION_IDS } from './sonatas';
import { roleOf } from './sonatas/derive';
import { alignMatrix, printBar, similarity } from './forms';
import type { PieceData, SectionSpec } from './types';
import { Key } from '../music/key';
import type { Measure } from '../music/piece';
import { mod, pcNumber } from '../music/pitch';
import { buildView, PieceView } from '../ui/model';

/**
 * Returns: every time a rondo's refrain comes back, or every variation of a theme, lined up bar by
 * bar under the first statement. Bars are compared twice: by their top and bass notes (does the
 * surface come back?) and by their harmony, beat by beat (does the path of chords come back?).
 * Deterministic, like the rest of the atlas.
 */

export type ReturnKind = 'same' | 'moved' | 'decorated' | 'changed' | 'missing';

export interface ReturnCell {
  /** The bar of this return aligned with reference bar `col`, or null. */
  bar: number | null;
  notes: number;
  harmony: number;
  shift: number;
  kind: ReturnKind;
}

export interface ReturnRow {
  section: SectionSpec;
  label: string;
  key: Key | null;
  cells: ReturnCell[];
  /** Bars of this return with no counterpart in the first statement. */
  added: number[];
  counts: Record<ReturnKind, number>;
}

export interface ReturnsRoom {
  id: string;
  kind: 'refrain' | 'variations';
  piece: PieceData;
  view: PieceView;
  reference: { section: SectionSpec; label: string; bars: Measure[] };
  rows: ReturnRow[];
}

const SAME = 0.75;
const HARMONY_SAME = 0.6;
const MATCH_FLOOR = 0.3;

/** Chord root (pitch class) and quality at each quarter of a bar. */
function harmonyPrint(view: PieceView, m: Measure): Array<[number, string] | null> {
  const out: Array<[number, string] | null> = [];
  for (let q = 0; q < Math.round(m.length); q++) {
    const t = m.start + q + 1e-6;
    let ev = null;
    for (const e of view.events) if (e.start <= t) ev = e;
    out.push(ev ? [pcNumber(ev.chord.root), ev.chord.triad] : null);
  }
  return out;
}

function harmonySimilarity(a: Array<[number, string] | null>, b: Array<[number, string] | null>): { sim: number; shift: number } {
  const n = Math.min(a.length, b.length);
  if (!n) return { sim: 0, shift: 0 };
  let best = { sim: -1, shift: 0 };
  for (let t = 0; t < 12; t++) {
    let hit = 0;
    for (let i = 0; i < n; i++) {
      const x = a[i];
      const y = b[i];
      // Same root and quality counts fully; same root in the other mode (a minore variation) counts most of the way.
      if (x && y && mod(x[0] + t, 12) === y[0]) hit += x[1] === y[1] ? 1 : 0.75;
    }
    const sim = hit / Math.max(a.length, b.length);
    if (sim > best.sim + 1e-9) best = { sim, shift: t };
  }
  return best;
}

const labelOf = (s: SectionSpec, i: number, kind: 'refrain' | 'variations') => {
  if (kind === 'variations') return i === 0 ? 'Theme' : s.label[0].toUpperCase() + s.label.slice(1);
  return ['First time', 'Second time', 'Third time', 'Fourth time', 'Fifth time', 'Sixth time'][i] ?? `Time ${i + 1}`;
};

function keyAt(view: PieceView, t: number): Key | null {
  let k: Key | null = null;
  for (const ev of view.events) if (ev.start <= t + 1e-6) k = ev.key;
  return k;
}

/** The statements to compare: refrain sections (letter A) or the theme and its variations. */
function statements(piece: PieceData): { kind: 'refrain' | 'variations'; sections: SectionSpec[] } | null {
  const secs = piece.sections ?? [];
  const theme = secs.findIndex((s) => roleOf(s.label).role === 'theme');
  if (theme >= 0) {
    const vars = secs.filter((s) => roleOf(s.label).role === 'variation');
    if (vars.length) return { kind: 'variations', sections: [secs[theme], ...vars] };
  }
  // The second half of a rounded binary refrain (B + A') is not a return of the refrain.
  const refrains = secs.filter((s) => {
    const r = roleOf(s.label);
    return r.role !== 'second-half' && r.letter?.replace(/'+$/, '') === 'A';
  });
  if (refrains.length >= 2) return { kind: 'refrain', sections: refrains };
  return null;
}

export function returnsIds(): string[] {
  return [...SONATA_IDS, ...VARIATION_IDS].filter((id) => {
    const p = derivedSonata(id)?.piece;
    return !!p && statements(p) !== null;
  });
}

const rooms = new Map<string, ReturnsRoom | null>();
export function returnsRoom(id: string): ReturnsRoom | null {
  if (rooms.has(id)) return rooms.get(id)!;
  let room: ReturnsRoom | null = null;
  const piece = derivedSonata(id)?.piece;
  const st = piece ? statements(piece) : null;
  if (piece && st) {
    const view = buildView(piece);
    // A short opening bar (an upbeat) has no counterpart in returns that start on the downbeat: leave it out of every statement.
    const barsOf = (s: SectionSpec) => {
      const bars = piece.measures.filter((m) => m.start >= s.start - 1e-6 && m.start < s.end - 1e-6);
      return bars.length > 1 && bars[0].length < bars[1].length - 1e-6 ? bars.slice(1) : bars;
    };
    const [ref, ...rest] = st.sections;
    const refBars = barsOf(ref);
    const refNotes = refBars.map((m) => printBar(piece.notes, m));
    const refHarm = refBars.map((m) => harmonyPrint(view, m));
    const rows: ReturnRow[] = rest.map((sec, k) => {
      const bars = barsOf(sec);
      const notes = bars.map((m) => printBar(piece.notes, m));
      const harm = bars.map((m) => harmonyPrint(view, m));
      const pairs = refBars.map((_, i) =>
        bars.map((__, j) => {
          const a = similarity(refNotes[i], notes[j]);
          const h = harmonySimilarity(refHarm[i], harm[j]);
          return { a, h };
        }),
      );
      // alignMatrix subtracts its own floor (0.35); shifting by 0.05 makes the effective floor MATCH_FLOOR.
      const aligned = alignMatrix(pairs.map((row) => row.map(({ a, h }) => ({ sim: Math.max(a.sim, 0.9 * h.sim) + (0.35 - MATCH_FLOOR), shift: a.sim >= h.sim ? a.shift : h.shift }))));
      const cells: ReturnCell[] = refBars.map(() => ({ bar: null, notes: 0, harmony: 0, shift: 0, kind: 'missing' as ReturnKind }));
      const added: number[] = [];
      for (const p of aligned) {
        if (p.i !== null && p.j !== null) {
          const { a, h } = pairs[p.i][p.j];
          const kind: ReturnKind = a.sim >= SAME ? (a.shift === 0 ? 'same' : 'moved') : h.sim >= HARMONY_SAME ? 'decorated' : 'changed';
          cells[p.i] = { bar: bars[p.j].bar, notes: Math.round(a.sim * 100) / 100, harmony: Math.round(h.sim * 100) / 100, shift: a.sim >= SAME ? a.shift : h.shift, kind };
        } else if (p.j !== null) added.push(bars[p.j].bar);
      }
      const counts = { same: 0, moved: 0, decorated: 0, changed: 0, missing: 0 } as Record<ReturnKind, number>;
      cells.forEach((c) => counts[c.kind]++);
      return { section: sec, label: labelOf(sec, k + 1, st.kind), key: keyAt(view, sec.start), cells, added, counts };
    });
    room = { id, kind: st.kind, piece, view, reference: { section: ref, label: labelOf(ref, 0, st.kind), bars: refBars }, rows };
  }
  rooms.set(id, room);
  return room;
}

export const RETURN_WORDS: Record<ReturnKind, { word: string; name: string }> = {
  same: { word: 'As the first time', name: 'Literal repetition' },
  moved: { word: 'The same, in another key', name: 'Transposed repetition' },
  decorated: { word: 'Same harmony, new surface', name: 'Ornamented or varied surface over the same harmony' },
  changed: { word: 'Rewritten', name: 'Altered harmony and surface' },
  missing: { word: 'Left out', name: 'Omitted' },
};

export const RETURN_LETTER: Record<ReturnKind, string> = { same: '=', moved: 'M', decorated: 'D', changed: '~', missing: '-' };
export const LETTER_RETURN: Record<string, ReturnKind> = { '=': 'same', M: 'moved', D: 'decorated', '~': 'changed', '-': 'missing' };
